import { Phase } from './Phases';
import { Token } from './Resources';
import { Bid } from './Rules';
import { CounterDelta } from './Utility';

export type GameEvent = GameEventData & {
  timestamp: number;
};
export type GameEventOfType<T extends EventTypes> = Extract<
  GameEvent,
  { type: T }
>;

export enum EventTypes {
  // SET_UP_GAME = 'SET_UP_GAME',

  ASSIGN_LEADER = 'ASSIGN_LEADER',

  // internal
  SPAWN_CARDS = 'SPAWN_CARDS', // [cardID, index][]
  SPAWN_TRIBUTES = 'SPAWN_TRIBUTES', // [cardID, index][]
  SETUP_TRIBUTE_DECK = 'SETUP_TRIBUTE_DECK', // cardID[]
  SETUP_DECK = 'SETUP_DECK', // cardID[]
  SETUP_BASIC_PILE = 'SETUP_BASIC_PILE', // cardID[]
  TRIBUTE_PLANNING_DATA = 'TRIBUTE_PLANNING_DATA',
  TRIBUTE_RESOLUTION_DATA = 'TRIBUTE_RESOLUTION_DATA',
  DEBUG_SET_PLAYER_COUNTERS = 'DEBUG_SET_PLAYER_COUNTERS',
  DEBUG_ADD_CARD = 'DEBUG_ADD_CARD',

  SET_UP_AGE = 'SET_UP_AGE',
  SET_UP_TURN = 'SET_UP_TURN',
  SET_UP_PHASE = 'SET_UP_PHASE',
  // only used timing
  END_OF_PHASE = 'END_OF_PHASE',
  // END_OF_TURN - unused
  END_OF_AGE = 'END_OF_AGE',
  END_OF_GAME = 'END_OF_GAME',

  TRIBUTES_RESHUFFLED = 'TRIBUTES_RESHUFFLED',

  DEAL_CARD = 'DEAL_CARD',
  DEAL_TRIBUTE = 'DEAL_TRIBUTE',

  DISCARD_CARD = 'DISCARD_CARD',
  DISCARD_TRIBUTE = 'DISCARD_TRIBUTE',

  PRODUCTION = 'PRODUCTION',
  CONFLICT_RESULTS = 'CONFLICT_RESULTS',

  // TODO(ldg2): add this
  // UPGRADE_CARD = 'UPGRADE_CARD',

  BID = 'BID',
  // RESOLVE = 'RESOLVE',

  PURCHASE_CARD = 'PURCHASE_CARD', // pay and add card to board
  GAIN_CARD = 'GAIN_CARD', // gain card triggers

  EARN_TOKEN = 'EARN_TOKEN',
  REFUND_MILITARY = 'REFUND_MILITARY',

  GAIN_TRIBUTES = 'GAIN_TRIBUTES',

  // END_GAME_SCORING = 'END_GAME_SCORING',
  END_GAME_EFFECTS = 'END_GAME_EFFECTS',

  // ----- DEPRECATED -----
  GAIN_LEADER = 'GAIN_LEADER', // deprecated: use ASSIGN_LEADER + GAIN_CARD
}

export type ConflictResultsPayload = {
  cardID: string;
  winningPlayerID: string | null; // null when NoDicePlus mode results in a tie
  rounds: ConflictRoundInfo[];
};

export type ConflictRoundInfo = {
  [userID: string]: ConflictRoundPlayerInfo;
};

export type ConflictRoundPlayerInfo = {
  playerID: string;
  rolls: number[];
  bonus: number;
  total: number;
};
export type TriggeredCardInfo = {
  cardID: string;
  counters: Partial<CounterDelta>;
};

export type GameEventData =
  | {
      type: EventTypes.SPAWN_CARDS;
      payload: {
        cardIDDefIDPairs: [string, string][];
      };
    }
  | {
      type: EventTypes.SPAWN_TRIBUTES;
      payload: {
        cardIDDefIDPairs: [string, string][];
      };
    }
  | {
      type: EventTypes.DEBUG_SET_PLAYER_COUNTERS;
      payload: {
        userID: string;
        counters: Partial<CounterDelta>;
      };
    }
  | {
      type: EventTypes.DEBUG_ADD_CARD;
      payload: {
        userID: string;
        cardID: string;
      };
    }
  | {
      type: EventTypes.SETUP_DECK;
      payload: {
        cardIDs: string[];
      };
    }
  | {
      type: EventTypes.SETUP_TRIBUTE_DECK;
      payload: {
        cardIDs: string[];
      };
    }
  | {
      type: EventTypes.SETUP_BASIC_PILE;
      payload: {
        cardIDs: string[];
      };
    }
  | {
      type: EventTypes.TRIBUTE_PLANNING_DATA;
      payload: {
        planningStateByTributeID: {
          [tributeID: string]: any;
        };
      };
    }
  | {
      type: EventTypes.GAIN_LEADER;
      payload: {
        userID: string;
        cardID: string;

        counters: Partial<CounterDelta>;
      };
    }
  | {
      type: EventTypes.ASSIGN_LEADER;
      payload: {
        userID: string;
        cardID: string;
      };
    }
  | {
      type: EventTypes.SET_UP_AGE;
      payload: {
        age: number;
      };
    }
  | {
      type: EventTypes.SET_UP_TURN;
      payload: {
        turn: number;
      };
    }
  | {
      type: EventTypes.SET_UP_PHASE;
      payload: {
        phase: Phase;
      };
    }
  | {
      type: EventTypes.TRIBUTES_RESHUFFLED;
      payload: {};
    }
  | {
      type: EventTypes.DEAL_CARD;
      payload: {
        cardID: string;
      };
    }
  | {
      type: EventTypes.DEAL_TRIBUTE;
      payload: {
        cardID: string;
      };
    }
  | {
      type: EventTypes.END_OF_PHASE;
      payload: {
        elapsedPhaseMillisByPlayerID: { [k: string]: number };
      };
    }
  | {
      type: EventTypes.PRODUCTION;
      payload: {
        userID: string;
        counters: Partial<CounterDelta>;
      };
    }
  | {
      type: EventTypes.BID;
      payload: {
        userID: string;
        bid: Bid;

        // NOTE: this could be migrated to a counter delta
        spentMilitary: number;

        // Informational
        cardID: string;
      };
    }
  | {
      type: EventTypes.PURCHASE_CARD;
      payload: {
        userID: string;
        cardID: string;
        counters: Partial<CounterDelta>;

        // Informational
        spentCounters: Partial<CounterDelta>;
        triggeredCards: TriggeredCardInfo[];
      };
    }
  | {
      type: EventTypes.GAIN_CARD;
      payload: {
        userID: string;
        cardID: string;
        counters: Partial<CounterDelta>;

        // Informational (cardCounters includes prophecy favor for gained card)
        cardCounters: Partial<CounterDelta>;
        // triggeredCards includes both gain-other triggers and prophecy deltas from existing cards
        triggeredCards: TriggeredCardInfo[];
      };
    }
  | {
      type: EventTypes.DISCARD_CARD;
      payload: {
        cardID: string;
      };
    }
  | {
      type: EventTypes.DISCARD_TRIBUTE;
      payload: {
        cardID: string;
      };
    }
  | {
      type: EventTypes.GAIN_TRIBUTES;
      payload: {
        userID: string;
        counters: Partial<CounterDelta>;

        // informational
        favor: number;
        tributeTokens: number;
        tributeIDs: string[];
        triggeredCards: TriggeredCardInfo[];
      };
    }
  | {
      type: EventTypes.END_OF_AGE;
      payload: {};
    }
  | {
      type: EventTypes.END_OF_GAME;
      payload: {
        // informational
        winningUserIDs: string[];
        endOfGameFavorByUserID: { [userID: string]: number };
      };
    }
  | {
      type: EventTypes.END_GAME_EFFECTS;
      payload: {
        userID: string;
        counters: Partial<CounterDelta>;

        // informational
        triggeredCards: TriggeredCardInfo[];
      };
    }
  | {
      type: EventTypes.CONFLICT_RESULTS;
      payload: ConflictResultsPayload;
    }
  | {
      type: EventTypes.EARN_TOKEN;
      payload: {
        userID: string;
        token: Token;
        counters: Partial<CounterDelta>;

        // informational
        triggeredCards: TriggeredCardInfo[];
      };
    }
  | {
      type: EventTypes.REFUND_MILITARY;
      payload: {
        userID: string;
        refundedMilitary: number;
      };
    };
// | {
//     type: EventTypes2.END_GAME_SCORING;
//     payload: {
//       userID: string;
//       cost: CounterDelta;
//     } & CounterDelta;
//   }
